import { useState } from "react";

import { Colors, Portal } from "@blueprintjs/core";
import styled from "@emotion/styled";
import { Swiper as SwiperType } from "swiper";
import "swiper/css";
import "swiper/css/navigation";

import { Navigation } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";

import { Box, Flex } from "@/components/layout/flexbox";
import { CoachProfile } from "@/components/pages_flexible/coach/CoachProfile";
import { BackDrop } from "@/components/pieces/BackDrop";
import { Clickable } from "@/components/pieces/interaction/Clickable";
import { OnboardButton } from "@/components/pieces/onboarding/CtaButton";
import { PORTAL_Z_INDEX } from "@/css/constants";
import { ListAgentProfile } from "@/types/coaches";

interface CoachProfilesOverlayProps {
  coachProfiles: ListAgentProfile[];
  activeProfile?: ListAgentProfile;
  onClose: () => void;
  onSelect: (coachId: string) => void;
  selectedCoachId?: string;
}

export function CoachProfilesOverlay({
  coachProfiles,
  activeProfile,
  onClose,
  onSelect,
  selectedCoachId,
}: CoachProfilesOverlayProps) {
  return (
    <Portal>
      <BackDrop
        visible={!!activeProfile}
        zIndex={PORTAL_Z_INDEX}
        opacity={0.8}
      />
      {activeProfile ? (
        <Flex
          justifyContent="center"
          style={{
            position: "fixed",
            top: 0,
            bottom: 0,
            left: 0,
            right: 0,
            overflowY: "auto",
            overflowX: "hidden",
            zIndex: PORTAL_Z_INDEX,
          }}
          onClick={onClose}
        >
          <ProfileCarousel
            coachProfiles={coachProfiles}
            activeProfile={activeProfile}
            onClose={onClose}
            onSelect={onSelect}
            selectedCoachId={selectedCoachId}
          />
        </Flex>
      ) : null}
    </Portal>
  );
}

interface ProfileCarouselProps {
  coachProfiles: ListAgentProfile[];
  activeProfile?: ListAgentProfile;
  onClose: () => void;
  onSelect: (coachId: string) => void;
  selectedCoachId?: string;
}

function ProfileCarousel({
  coachProfiles,
  activeProfile,
  onClose,
  onSelect,
  selectedCoachId,
}: ProfileCarouselProps) {
  const initialSlide = activeProfile ? coachProfiles.indexOf(activeProfile) : 0;
  const [currentSlide, setCurrentSlide] = useState(initialSlide);
  const [currentPlayingId, setCurrentPlayingId] = useState<string>();
  const [swiperInstance, setSwiperInstance] = useState<SwiperType>();

  return (
    <CarouselContainer>
      <StyledSwiper
        modules={[Navigation]}
        speed={500}
        centeredSlides={true}
        initialSlide={initialSlide}
        slidesPerView="auto"
        spaceBetween={30}
        onSwiper={setSwiperInstance}
        onSlideChange={(swiper) => {
          setCurrentSlide(swiper.activeIndex);
          setCurrentPlayingId(undefined);

          // Focus the CTA button after slide change
          const activeSlide = swiper.slides[swiper.activeIndex];
          const ctaButton = activeSlide?.querySelector(
            ".ctaBtn",
          ) as HTMLElement;
          if (ctaButton) {
            ctaButton.focus();
          }
        }}
      >
        {coachProfiles.map((profile, index) => {
          const { user, id } = profile;
          const active = currentSlide === index;

          if (!user) return null;

          return (
            <SwiperSlide key={id} className={active ? "activeSlide" : ""}>
              <Box mt={4} px={3}>
                <Box
                  style={{
                    marginTop: active ? 0 : 60,
                    transition: "all 500ms ease",
                    position: "relative",
                    display: "table",
                  }}
                  pb={3}
                  onClick={(e) => e.stopPropagation()}
                >
                  <CoachProfile
                    onClose={active ? onClose : undefined}
                    profile={profile}
                    videoPlaying={id === currentPlayingId}
                    onVideoClick={() => {
                      currentPlayingId === id
                        ? setCurrentPlayingId(undefined)
                        : setCurrentPlayingId(id);
                    }}
                    action={
                      <OnboardButton
                        intent="primary"
                        className="ctaBtn"
                        large={true}
                        onClick={() => onSelect(user.id)}
                        disabled={!active || !!selectedCoachId}
                        loading={selectedCoachId === user.id}
                      >
                        Work with {user.firstName}
                      </OnboardButton>
                    }
                    skeleton={!active}
                  />
                  <Clickable
                    onClick={(e) => {
                      e.stopPropagation();
                      if (!active && swiperInstance) {
                        swiperInstance.slideTo(index);
                      }
                    }}
                  >
                    <Box
                      style={{
                        transition: "opacity 300ms ease",
                        position: "absolute",
                        top: 0,
                        left: 0,
                        bottom: 0,
                        right: 0,
                        backgroundColor: Colors.BLACK,
                        opacity: active ? 0 : 0.5,
                        pointerEvents: active ? "none" : "all",
                      }}
                    />
                  </Clickable>
                </Box>
              </Box>
            </SwiperSlide>
          );
        })}
      </StyledSwiper>
    </CarouselContainer>
  );
}

const CarouselContainer = styled.div`
  position: relative;
  width: 100%;
`;

const StyledSwiper = styled(Swiper)`
  overflow: hidden;

  .swiper-slide {
    width: auto;
    max-width: 100%;
  }
`;
